// Copyright (c) Ping. All rights reserved.

import React, { useMemo } from 'react';
import {
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
// Class-aware scheme hook — NOT RN's `useColorScheme` (OS `prefers-color-scheme`
// reads the wrong palette under the app's class-based theme; repo styling rule 3).
import { useColorScheme } from '../foundations/colorScheme';
import { Avatar } from '../components/Avatar';
import { createTheme } from '../design-system/theme';
import { threadsStrings } from './strings';
import { visibleParticipants } from './threadSummary';
import { THREAD_PARTICIPANT_STRIP_VISIBLE_LIMIT, type ThreadParticipant } from './types';

export interface ThreadParticipantStripProps {
  readonly participants: readonly ThreadParticipant[];
  readonly visibleLimit?: number;
  readonly testID?: string;
}

const AVATAR_OVERLAP = -6;

/**
 * Compact avatar row used at the top of `ThreadOpenPanel` to surface
 * recent participants. Caps visible avatars and rolls extras into a
 * "+N" badge per the wave-2 design.
 */
export const ThreadParticipantStrip = React.memo<ThreadParticipantStripProps>(
  ({ participants, visibleLimit = THREAD_PARTICIPANT_STRIP_VISIBLE_LIMIT, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const theme = createTheme(colorScheme);

    const { visible, overflowCount } = useMemo(
      () => visibleParticipants(participants, visibleLimit),
      [participants, visibleLimit],
    );

    if (visible.length === 0 && overflowCount === 0) {
      return null;
    }

    return (
      <View
        style={styles.container}
        accessibilityRole="text"
        accessibilityLabel={threadsStrings.ping_threads_participants_label}
        testID={testID}
      >
        {visible.map((p, index) => (
          <View
            key={p.userId}
            style={[styles.avatarSlot, index > 0 ? { marginLeft: AVATAR_OVERLAP } : null]}
          >
            <Avatar
              size="small"
              {...(p.imageUri !== null ? { imageUri: p.imageUri } : {})}
              initials={p.displayName}
            />
          </View>
        ))}
        {overflowCount > 0 && (
          <View
            style={[
              styles.overflowBadge,
              {
                backgroundColor: theme.colors.surface.tertiary,
                borderColor: theme.colors.surface.primary,
                marginLeft: visible.length > 0 ? AVATAR_OVERLAP : 0,
              },
            ]}
          >
            <Text
              style={[styles.overflowText, { color: theme.colors.text.secondary }]}
              numberOfLines={1}
            >
              {threadsStrings.ping_threads_participants_overflow(overflowCount)}
            </Text>
          </View>
        )}
      </View>
    );
  },
);

ThreadParticipantStrip.displayName = 'ThreadParticipantStrip';

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
  } satisfies ViewStyle,
  avatarSlot: {
    borderRadius: 16,
    overflow: 'hidden',
  } satisfies ViewStyle,
  overflowBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
  } satisfies ViewStyle,
  overflowText: {
    fontSize: 11,
    fontWeight: '700',
  } satisfies TextStyle,
});
